/**
 * Stitching the pieces of one кружочек into the file that is sent.
 *
 * A кружочек is recorded straight off the camera track, because a canvas between
 * the camera and the encoder is what was freezing them on iOS (see
 * startVideoNote in capture.ts). The cost of that fix was the flip button: a
 * MediaRecorder is bound to the tracks it was handed, and there is no swapping
 * one under it. So the take is cut instead — each flip closes one recorder and
 * opens the next on the other camera — and what the sender is handed back is
 * this function's job: the segments, end to end, as a single MP4.
 *
 * This is the same machinery videoCompress.ts uses on a clip picked from the
 * gallery, and it is possible for the same reason: WebCodecs hands the decode
 * and the encode to whatever codec the phone has, at several times realtime, and
 * mediabunny does the demuxing and muxing WebCodecs deliberately does not. Read
 * that file's opening for the argument in full; it applies here unchanged.
 *
 * **Unlike that file, everything here is fatal.** compressVideo can always fall
 * back to sending the original, because there is one. A half-joined кружочек is
 * not a кружочек, and choosing a fragment to send on the sender's behalf would
 * be picking which half of what they filmed to throw away. So this throws and
 * the take is lost, with the composer saying so — which is also why the flip
 * button is only offered where `canJoinSegments()` is true.
 */

import type { Quality as QualityClass, VideoCodec } from 'mediabunny';

/** One piece of a take: what MediaRecorder wrote, and how long it ran. */
export interface Segment {
  blob: Blob;
  durationMs: number;
}

export interface JoinQuality {
  videoBitsPerSecond: number;
  audioBitsPerSecond: number;
}

/**
 * How often a key frame is written, in seconds — the same two as
 * videoCompress.ts, and for the same reason: fewer is a smaller file and a video
 * that jumps when it is scrubbed, more is the opposite.
 */
const KEY_FRAME_SECONDS = 2;

/**
 * True when this browser has the two halves of WebCodecs a join needs.
 *
 * Deliberately the same test `canCompressVideo` makes, and deliberately not a
 * call into it: the two happen to agree today because both want a decoder and an
 * encoder, but one is about a clip from the gallery and the other about whether
 * a take may be cut in half at all. A browser that fails this is not offered the
 * flip button, so no take can be created that this file would then have to lose.
 */
export function canJoinSegments(): boolean {
  return (
    typeof globalThis !== 'undefined' &&
    'VideoEncoder' in globalThis &&
    'VideoDecoder' in globalThis
  );
}

/**
 * The segments as one MP4, in the order they were filmed.
 *
 * Timestamps are shifted by the running total rather than preserved, so the
 * pieces meet end to end and the half-second the phone spends letting go of one
 * camera and opening the other is closed rather than held as a frozen frame. It
 * is a cut, which is what a flip looks like in every other app that has one.
 *
 * `onProgress` runs from 0 to 1 and is called with 0 before any work starts —
 * the record pill uses that first call as the signal to say «Собираем», so a
 * take with nothing to join never flashes it.
 */
export async function joinSegments(
  segments: Segment[],
  quality: JoinQuality,
  onProgress?: (fraction: number) => void,
): Promise<Blob> {
  if (segments.length === 0) throw new Error('нечего собирать');
  onProgress?.(0);

  // Loaded on demand for the reason compressVideo's own import spells out: the
  // demuxer and muxer are larger than this app's whole bundle, and belong off
  // the path between opening the page and drawing a chat list.
  const {
    ALL_FORMATS,
    BlobSource,
    BufferTarget,
    EncodedAudioPacketSource,
    EncodedPacketSink,
    Input,
    Mp4OutputFormat,
    Output,
    Quality,
    VideoSampleSink,
    VideoSampleSource,
    canEncodeVideo,
  } = await import('mediabunny');

  const inputs = segments.map(
    (segment) => new Input({ formats: ALL_FORMATS, source: new BlobSource(segment.blob) }),
  );

  try {
    const first = inputs[0]!;
    const firstVideo = await first.getPrimaryVideoTrack();
    if (!firstVideo) throw new Error('в первой части нет картинки');

    /**
     * The box every frame is put into, taken from the first piece and rounded
     * to even numbers.
     *
     * Even because H.264 stores chroma at half resolution and has nowhere to
     * put an odd last row — the same rounding `fitInside` does in
     * videoCompress.ts. Fixed for the whole file because an encoder's frame is
     * decided by the first sample it sees, and the two cameras of a phone need
     * not offer the same modes; a later piece is scaled into this box and
     * cropped, which is what the circle does to the picture anyway.
     */
    const even = (value: number) => Math.max(2, Math.round(value / 2) * 2);
    // Asked for, not read off the track. `displayWidth` is a deprecated
    // synchronous getter that does not merely return a stale number when the
    // container has not yielded one yet — it *throws*, telling you to use the
    // async method, and which of the two happens is a property of the file
    // rather than of this code. A take that always failed at the first line
    // that touched the picture, on one platform and never on the other, is
    // exactly the shape of that.
    const width = even(await firstVideo.getDisplayWidth());
    const height = even(await firstVideo.getDisplayHeight());

    const format = new Mp4OutputFormat({
      // Metadata at the front, as compressVideo also asks for: a кружочек is
      // played out of an HTTP range request, and an MP4 with its index at the
      // end has to be fetched whole before the first frame can be shown.
      fastStart: 'in-memory',
    });
    const output = new Output({ format, target: new BufferTarget() });

    /**
     * The codec and the bitrate together, asked of the device rather than
     * assumed — and asked as the *whole* configuration, which is the part that
     * matters.
     *
     * H.264 is what every phone decodes and what the recording itself is written
     * in, so it is asked for first. But a browser that has WebCodecs is not a
     * browser that can encode any particular codec, and a codec it can encode is
     * not one it will encode at any bitrate: this browser refuses AAC at the 68
     * kbps a кружочек's soundtrack is configured with, and says so only when the
     * encoder is configured — by which time there is a take to lose. Asking with
     * a codec and no bitrate is therefore a probe that proves nothing, which is
     * how a check that passed everywhere still lost every flipped take on iOS.
     *
     * The rates are tried from what was asked for upwards. A кружочек is a
     * minute at most and the sound is a twelfth of it, so a device that insists
     * on 128 kbps costs a few tens of kilobytes rather than an argument.
     */
    const videoCodec = await pickCodec<VideoCodec>(
      (['avc', 'hevc', 'vp9', 'av1'] as VideoCodec[]).filter((codec) =>
        format.getSupportedVideoCodecs().includes(codec),
      ),
      [quality.videoBitsPerSecond, 1_200_000, 2_000_000],
      (codec, rate) =>
        canEncodeVideo(codec, { width, height, quality: bitrate(Quality, rate) }),
    );
    if (!videoCodec) throw new Error(`нечем кодировать картинку ${width}×${height}`);

    const video = new VideoSampleSource({
      codec: videoCodec.codec,
      quality: bitrate(Quality, videoCodec.bitsPerSecond),
      keyFrameInterval: KEY_FRAME_SECONDS,
      // Both, and neither is redundant. `sizeChangeBehavior` is the permission
      // for the samples to change shape at all — without it the second piece is
      // refused outright, since a кружочек filmed on two cameras is two shapes
      // by definition. The transform is what they change *into*: the even box
      // above rather than whatever the first frame happened to be, which is the
      // odd-dimension refusal that lost every flipped take on iOS.
      sizeChangeBehavior: 'cover',
      // Cropped rather than letterboxed — 'contain' would put black bars inside
      // the circle.
      transform: { width, height, fit: 'cover' },
    });
    output.addVideoTrack(video);

    /**
     * The soundtrack is **copied, not re-encoded** — and that is the difference
     * between this working on every phone and working on one of them.
     *
     * The picture has to be re-encoded: the two cameras of a phone hand over
     * two different shapes and only an encoder can make them one. The sound has
     * no such problem. Every piece of a take comes from the same microphone
     * through the same gain stage into a MediaRecorder configured identically,
     * so the pieces are already one continuous soundtrack cut into parts —
     * their packets need a new timestamp and nothing else.
     *
     * Re-encoding it was what lost every flipped take on iOS. Safari has the
     * video half of WebCodecs and not the audio half, so `canEncodeAudio` says
     * no to every codec at every bitrate, and the join stopped at the first
     * thing it could not do — on a soundtrack that never needed doing. Copying
     * is also better where an encoder does exist: no second generation of lossy
     * audio, and no time spent on it.
     *
     * A кружочек always has sound, since camera and microphone come out of one
     * getUserMedia call and a take without the second one does not start (see
     * openBoth). Asked of the file all the same — adding a track the pieces
     * cannot fill would produce a silent track rather than no track.
     */
    const firstAudio = await first.getPrimaryAudioTrack();
    const audioCodec = firstAudio ? await firstAudio.getCodec() : null;
    const audioConfig = firstAudio ? await firstAudio.getDecoderConfig() : null;
    let audio: InstanceType<typeof EncodedAudioPacketSource> | null = null;
    if (firstAudio) {
      if (!audioCodec || !audioConfig) throw new Error('звук записан неизвестно чем');
      if (!format.getSupportedAudioCodecs().includes(audioCodec)) {
        throw new Error(`звук записан как ${audioCodec}, а он не ложится в mp4`);
      }
      audio = new EncodedAudioPacketSource(audioCodec);
      output.addAudioTrack(audio);
    }

    await output.start();

    /** Where the next segment begins in the finished file, in seconds. */
    let offset = 0;
    /** The audio decoder config travels with the first packet — see below. */
    let sentAudioConfig = false;
    const total = Math.max(
      0.001,
      segments.reduce((sum, segment) => sum + segment.durationMs, 0) / 1000,
    );

    for (const [index, input] of inputs.entries()) {
      const videoTrack = await input.getPrimaryVideoTrack();
      if (!videoTrack) throw new Error(`в части ${index + 1} нет картинки`);

      for await (const sample of new VideoSampleSink(videoTrack).samples()) {
        try {
          sample.setTimestamp(sample.timestamp + offset);
          await video.add(sample);
          // Video only. The audio pass below walks the same seconds a second
          // time, and a bar that went backwards halfway through every segment
          // would be worse than one that moves in two speeds.
          onProgress?.(clamp01((offset + sample.timestamp) / total));
        } finally {
          sample.close();
        }
      }

      const audioTrack = audio ? await input.getPrimaryAudioTrack() : null;
      if (audio && audioTrack) {
        // A piece whose sound came out under a different configuration cannot
        // be copied into the same track — the decoder would be reconfigured
        // mid-file. It does not happen for a кружочек, where every piece shares
        // one microphone, but "it does not happen" is not something to write a
        // broken file over.
        const codec = await audioTrack.getCodec();
        if (codec !== audioCodec) {
          throw new Error(`звук части ${index + 1} записан как ${codec}, а не ${audioCodec}`);
        }
        for await (const packet of new EncodedPacketSink(audioTrack).packets()) {
          await audio.add(packet.clone({ timestamp: packet.timestamp + offset }), {
            // The decoder configuration goes with the first packet and only the
            // first: it is what the container writes into the track header.
            decoderConfig: sentAudioConfig ? undefined : (audioConfig ?? undefined),
          });
          sentAudioConfig = true;
        }
      }

      // The file's own duration rather than the measured one: what was recorded
      // is what has to be stepped over, and a segment whose last frame landed
      // early would otherwise leave a hole the next one starts inside of.
      offset += await input.computeDuration();
      onProgress?.(clamp01(offset / total));
    }

    video.close();
    audio?.close();
    await output.finalize();

    const buffer = output.target.buffer;
    if (!buffer || buffer.byteLength === 0) throw new Error('сборка не дала файла');

    onProgress?.(1);
    return new Blob([buffer], { type: 'video/mp4' });
  } finally {
    for (const input of inputs) input.dispose();
  }
}

/**
 * The first codec and bitrate this device says it will actually encode.
 *
 * Codecs in the caller's order of preference, rates from what was asked for
 * upwards — the pairing matters, since a refusal can be about either half or
 * about the combination, and only the whole configuration answers the question
 * the encoder will be asked later. Null when nothing on the list works, which
 * is a fact worth an error rather than a guess worth trying.
 */
export async function pickCodec<T>(
  codecs: T[],
  rates: number[],
  supported: (codec: T, bitsPerSecond: number) => Promise<boolean>,
): Promise<{ codec: T; bitsPerSecond: number } | null> {
  const wanted = [...new Set(rates)].filter((rate) => rate > 0);
  for (const codec of codecs) {
    for (const bitsPerSecond of wanted) {
      try {
        if (await supported(codec, bitsPerSecond)) return { codec, bitsPerSecond };
      } catch {
        // A probe that throws is a no, not a reason to stop asking.
      }
    }
  }
  return null;
}

/** bits a second as the Quality mediabunny wants, at the one call shape both sources take. */
function bitrate(Quality: typeof QualityClass, bitsPerSecond: number): QualityClass {
  return new Quality({ bitrate: bitsPerSecond, bitrateMode: 'variable' });
}

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}
